import React, { memo, useEffect, useState } from 'react';
import { MyProfileSecurityQuestionsActions } from '../_store/MyProfile.slice';
import { useDispatch, useSelector } from 'react-redux';
import '../css/MyProfile.css';
import CustomInput from '../components/common/CustomInput';
import CustomKeyboard from '../components/common/CustomKeyboard';
import Button from '../components/common/ui/Button';
import ScrollContainer from '../components/common/ScrollContainer';
import MyProfileSchema from '../validation/MyProfileSchema';
import TranslateFile from '../Loc/TranslateFile';
import { showAlert } from '../components/common/showAlert';
import useKioskRestrictIntervalValidation from '../hooks/useKioskRestrictIntervalValidation';
import { getStoredValue } from "../../src/_helpers/storageHelper";

const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = String(hours % 12 || 12).padStart(2, '0');
  return `${day}-${month}-${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
};

const MyProfile = () => {
  const dispatch = useDispatch();
  const [showBoard, setShowBoard] = useState(false);
  const updateSecurityQuestionResult = useSelector(state => state.MyProfile.updateInmateSecurityQuestion);
  const questions = useSelector((state) => state.MyProfile.questions);
  const [editableQuestions, setEditableQuestions] = useState([]);
  const loginResponse = useSelector(state => state.kioskLogin.loginResponse);
  const storedloginResponse = loginResponse || getStoredValue("loginResponse");
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [isUpdate, setisUpdate] = React.useState(false);
  const inmateId = loginResponse?.InmateID || storedloginResponse?.InmateID;
  const facilityId = loginResponse?.facilityID || storedloginResponse?.facilityID;
  const [errorMessage, setErrorMessage] = useState({});
  const [prevAnswer, setprevAnswer] = useState();
  const currentLang = TranslateFile();
  const isPageReloading = useSelector((state) => state.auth?.isPageReload);
  const storedLoginDetails = getStoredValue("loginDetails");
  const validateKioskRestrictInterval = useKioskRestrictIntervalValidation();

  useEffect(() => {
    if (inmateId && facilityId) {
      dispatch(MyProfileSecurityQuestionsActions.getInmateSecurityQuestions({ inmateId, facilityId }));
    }
  }, [dispatch, loginResponse, isPageReloading]);

  useEffect(() => {
    if (questions && questions.length > 0) {
      setEditableQuestions(questions.map(q => ({
        ...q,
        newAnswer: q.SQAnswer || '',
        editing: false
      })).sort((a, b) => new Date(a.ModifiedDate) - new Date(b.ModifiedDate)));
    }
  }, [questions]);

  const handleEditClick = (id) => {
    setEditableQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.QuestionID === id) {
          return { ...q, editing: !q.editing };
        } else {
          return { ...q, editing: false };
        }
      })
    );
    setEditingQuestionId(id);
  };

  const handleCancelClick = (id) => {
    setEditableQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q.QuestionID === id ? {
        ...q,
        editing: false,
        newAnswer: q.SQAnswer || '',
      } : q))
    );
  };

  const handleUpdateClick = async (id) => {
    const updatedQuestion = editableQuestions.find(q => q.QuestionID === id);
    setErrorMessage({});
    const { error } = MyProfileSchema(updatedQuestion);
    if (error) {
      error.details.forEach((e) => {
        setErrorMessage((prev) => ({
          ...prev,
          [e.path.toString()]: e.message,
        }));
      });
      return;
    }
    if (updatedQuestion) {
      const updatedQuestions = {
        InmatesId: inmateId,
        facilityID: facilityId,
        SQuestionID: updatedQuestion.QuestionID,
        SQAnswer: updatedQuestion.newAnswer,
        Action: 1,
        SQuestionSetID: updatedQuestion.QuestionSetID,
        SubmittedBy: 0,
        SubmitterID: (inmateId != null) ? (inmateId) : 0,
      };

      dispatch(MyProfileSecurityQuestionsActions.updateInmateSecurityQuestion(updatedQuestions));
      setisUpdate(true);
      setEditableQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q.QuestionID === id ? { ...q, editing: false } : q
        )
      );
    }
  };

  useEffect(() => {
    if (updateSecurityQuestionResult?.length !== 0 && isUpdate) {
      if (updateSecurityQuestionResult === 2) {
        showAlert(
          null,
          currentLang?.AlertPopup?.Message,
          currentLang?.AlertPopup?.Updated_Successfully,
          "success",
          "700px",
          currentLang?.Buttons?.Ok,
        );
      } else if (updateSecurityQuestionResult === 0) {
        showAlert(
          null,
           currentLang?.AlertPopup?.Message,
          currentLang?.AlertPopup?.Update_Failed,
          "error",
          "700px",
           currentLang?.Buttons?.Ok,
        );
      }
      setisUpdate(false);
      dispatch(MyProfileSecurityQuestionsActions.getInmateSecurityQuestions({ inmateId, facilityId }));
    }
  }, [updateSecurityQuestionResult, isUpdate]);

  const handleKeyPress = (button) => {
    if (button === "{accept}" || button === "{enter}" || button === "{prev}" || button === "{next}" || button === "{cancel}") {
      setShowBoard(false);
    }
    else if (button === "{backspace}") {
      setEditableQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q.QuestionID === editingQuestionId
            ? { ...q, newAnswer: q.newAnswer.slice(0, -1) } // Remove last character
            : q
        )
      );
    }
  };

  const handleKeyboardInputChange = (input) => {
    setEditableQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q.QuestionID === editingQuestionId ? { ...q, newAnswer: input } : q))
    );
  };

  useEffect(() => {
    validateKioskRestrictInterval(storedLoginDetails)
  }, []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="icon-container">
          <h3 className="find-inmate-head">{currentLang?.MyProfile?.Profile}</h3>
        </div>
        <div className="divider mb-3"></div>
        <ScrollContainer>
          {editableQuestions && editableQuestions?.length > 0 ? (
            editableQuestions?.map((q, index) => (
              <div key={q.QuestionID} className="profile-card">
                <p><strong>{currentLang?.MyProfile?.Last_Updated} </strong>{formatDateTime(q.ModifiedDate)}</p>
                <div className='row'>
                  <div className='col-md-8'>
                    <h4>{`${currentLang?.MyProfile?.Question} ${index + 1} : ${q.SecurityQuestion}`}</h4>
                  </div>
                  <div className='col-md-4 text-align-end'>
                    <button title='Edit' className="edit-btn " onClick={() => { handleEditClick(q.QuestionID); setErrorMessage({}) }}>
                      <i className="fa fa-edit" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <p>
                  <h5>{currentLang?.MyProfile?.Your_Answer}
                    {!q.editing ? (
                      <>
                        <span className='text-default-red font-size-14 mandatory-space'>{q.SQAnswer || 'No answer yet'}</span>

                      </>
                    ) : (
                      <>
                        <CustomInput
                          type="text"
                          value={q.newAnswer}
                          onClick={() => { setShowBoard(true); setprevAnswer(q.newAnswer) }} // Show keyboard on click
                          readOnly
                          errorMessage={errorMessage?.newAnswer}
                        />
                        {showBoard && (
                          <CustomKeyboard
                            onInputChange={handleKeyboardInputChange}
                            inputValue={editableQuestions.find(q => q.QuestionID === editingQuestionId)?.newAnswer || ''}
                            onKeyPress={handleKeyPress}
                            setShowBoard={setShowBoard}
                            placeholder={"Enter Your Answer"}
                            handleCancel={() => { handleKeyboardInputChange(prevAnswer); setShowBoard(false) }}
                            setErrorMessage={setErrorMessage}
                          />
                        )}
                      </>
                    )}
                  </h5>
                </p>
                {q.editing && (
                  <div className="mt-3 d-flex justify-content-center gap-3">
                    <Button
                      variant="primary"
                      className="me-2"
                      label={currentLang?.Buttons?.Update}
                      type={"submit"}
                      onClick={() => handleUpdateClick(q.QuestionID)}
                      status={"primary"}
                      disabled={false}
                    ></Button>
                    <Button
                      variant="secondary"
                      className="me-2"
                      onClick={() => handleCancelClick(q.QuestionID)}
                      label={currentLang?.Buttons?.Cancel}
                      type={"button"}
                      status={"secondary"}
                      disabled={false}
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="card-default">
              <span className="col-no-data">{currentLang?.No_Data_Found}</span>
            </div>
          )}
        </ScrollContainer>
      </div>
    </div>
  );
};

export default memo(MyProfile);
